import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import type { RiskStatus, Severity } from "@compliance-sage/contracts";
import { useI18n } from "../i18n";

export function Button({
  variant = "primary",
  busy = false,
  className = "",
  children,
  disabled,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "danger" | "ghost";
  busy?: boolean;
}) {
  const base =
    "inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-base font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";
  const variants: Record<string, string> = {
    primary: "bg-brand-600 text-white hover:bg-brand-700",
    secondary: "bg-white text-slate-700 border border-slate-300 hover:bg-slate-50",
    danger: "bg-red-600 text-white hover:bg-red-700",
    ghost: "text-slate-600 hover:bg-slate-100",
  };
  const onDark = variant === "primary" || variant === "danger";
  return (
    <button
      className={`${base} ${variants[variant]} ${className}`}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...props}
    >
      {busy ? (
        <span
          aria-hidden="true"
          className={`h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 ${
            onDark ? "border-white/30 border-t-white" : "border-slate-200 border-t-brand-600"
          }`}
        />
      ) : null}
      {children}
    </button>
  );
}

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-lg border border-slate-200 bg-white p-4 shadow-sm ${className}`}>
      {children}
    </div>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-base font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}

/** Compact labeled control for filter bars — small uppercase label above a control. */
export function FilterField({
  label,
  className = "",
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-600">{label}</span>
      {children}
    </label>
  );
}

const inputClass =
  "w-full rounded-md border border-slate-300 px-3 py-1.5 text-base focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500";

export function TextInput({ className = "", ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input className={`${inputClass} ${className}`} {...props} />;
}

/**
 * Checkbox that can also render the mixed state, so a group header can show
 * "some children selected" rather than lying with checked or unchecked.
 */
export function Checkbox({
  indeterminate = false,
  className = "",
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { indeterminate?: boolean }) {
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-checked={indeterminate ? "mixed" : undefined}
      className={`h-4 w-4 shrink-0 accent-brand-600 ${className}`}
      {...props}
    />
  );
}

function EyeIcon({ hidden }: { hidden: boolean }) {
  return hidden ? (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path
        fillRule="evenodd"
        d="M3.28 2.22a.75.75 0 0 0-1.06 1.06l14.5 14.5a.75.75 0 1 0 1.06-1.06l-2.034-2.035A9.96 9.96 0 0 0 18 10c-1.247-3.394-4.77-6-8-6a8.97 8.97 0 0 0-3.392.67L3.28 2.22ZM7.16 6.1 8.594 7.53a2.5 2.5 0 0 1 3.877 3.877l1.372 1.372A3.999 3.999 0 0 0 7.16 6.1Z"
        clipRule="evenodd"
      />
      <path d="m12.72 15.78-1.804-1.805a4 4 0 0 1-5.89-5.89L3.162 6.22A9.97 9.97 0 0 0 2 10c1.247 3.394 4.77 6 8 6a8.96 8.96 0 0 0 2.72-.22Z" />
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path d="M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
      <path
        fillRule="evenodd"
        d="M.664 10.59a1.651 1.651 0 0 1 0-1.186A10.004 10.004 0 0 1 10 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0 1 10 17c-4.257 0-7.893-2.66-9.336-6.41ZM14 10a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/** Password-style input with an eye control so a Super Admin can confirm the value. */
export function SecretInput({
  revealLabel,
  hideLabel,
  className = "",
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & {
  revealLabel: string;
  hideLabel: string;
}) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="relative">
      <input
        {...props}
        type={revealed ? "text" : "password"}
        className={`${inputClass} pr-10 font-mono ${className}`}
      />
      <button
        type="button"
        className="absolute inset-y-0 right-0 flex items-center px-2.5 text-slate-400 hover:text-slate-600"
        aria-label={revealed ? hideLabel : revealLabel}
        aria-pressed={revealed}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => setRevealed((current) => !current)}
      >
        <EyeIcon hidden={revealed} />
      </button>
    </div>
  );
}

/** Forwards its ref so callers can drive the caret (e.g. insert-at-cursor). */
export const TextArea = forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(function TextArea({ className = "", ...props }, ref) {
  return <textarea ref={ref} className={`${inputClass} min-h-[120px] ${className}`} {...props} />;
});

export function Select({ className = "", ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={`${inputClass} ${className}`} {...props} />;
}

export function RiskBadge({ risk }: { risk: RiskStatus | null }) {
  const { te } = useI18n();
  if (!risk) return <Badge tone="slate">-</Badge>;
  const tones: Record<RiskStatus, BadgeTone> = {
    PASS: "green",
    REVIEW_REQUIRED: "amber",
    HIGH_RISK: "red",
  };
  return <Badge tone={tones[risk]}>{te("riskStatus", risk)}</Badge>;
}

export function SeverityBadge({ severity }: { severity: Severity }) {
  const { te } = useI18n();
  const map: Record<Severity, BadgeTone> = {
    LOW: "slate",
    MEDIUM: "amber",
    HIGH: "red",
    CRITICAL: "red",
  };
  return <Badge tone={map[severity]}>{te("severity", severity)}</Badge>;
}

export type BadgeTone = "slate" | "green" | "amber" | "red" | "indigo";
export function Badge({ tone = "slate", children }: { tone?: BadgeTone; children: ReactNode }) {
  const tones: Record<BadgeTone, string> = {
    slate: "bg-slate-100 text-slate-700",
    green: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-700",
    red: "bg-red-100 text-red-700",
    indigo: "bg-indigo-100 text-indigo-700",
  };
  return (
    <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-sm font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
}

export function Spinner({ className = "" }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="loading"
      className={`inline-block h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600 ${className}`}
    />
  );
}

/** Full-width loading placeholder for a page or a route transition. */
export function PageLoading() {
  const { t } = useI18n();
  return (
    <div className="flex items-center justify-center gap-3 py-12 text-base text-slate-500">
      <Spinner />
      <span>{t("loading")}</span>
    </div>
  );
}

export function ErrorBanner({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      {message}
    </div>
  );
}

export type DropdownMenuItem = {
  label: string;
  onSelect: () => void;
  tone?: "default" | "danger";
  disabled?: boolean;
};

function DropdownChevron() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M4.22 6.22a.75.75 0 0 1 1.06 0L8 8.94l2.72-2.72a.75.75 0 1 1 1.06 1.06l-3.25 3.25a.75.75 0 0 1-1.06 0L4.22 7.28a.75.75 0 0 1 0-1.06Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

export function DropdownMenu({
  label,
  ariaLabel,
  items,
  disabled = false,
  busy = false,
  align = "end",
  triggerClassName,
}: {
  label: string;
  ariaLabel?: string;
  items: DropdownMenuItem[];
  disabled?: boolean;
  busy?: boolean;
  align?: "start" | "end";
  triggerClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const updatePosition = () => {
    const trigger = rootRef.current;
    const menu = menuRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const menuWidth = menu?.offsetWidth ?? 160;
    const menuHeight = menu?.offsetHeight ?? 0;
    const left =
      align === "end" ? Math.max(8, rect.right - menuWidth) : Math.min(rect.left, window.innerWidth - menuWidth - 8);
    let top = rect.bottom + 4;
    if (top + menuHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - menuHeight - 4);
    }
    setCoords({ top, left });
  };

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, align]);

  if (items.length === 0) return null;

  return (
    <>
      <span className="inline-flex" ref={rootRef}>
        {triggerClassName ? (
          <button
            type="button"
            className={triggerClassName}
            disabled={disabled || busy}
            aria-busy={busy || undefined}
            aria-label={ariaLabel}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={menuId}
            onClick={() => setOpen((current) => !current)}
          >
            {label}
            <DropdownChevron />
          </button>
        ) : (
          <Button
            type="button"
            variant="secondary"
            disabled={disabled}
            busy={busy}
            aria-label={ariaLabel}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={menuId}
            onClick={() => setOpen((current) => !current)}
          >
            {label}
            <DropdownChevron />
          </Button>
        )}
      </span>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              id={menuId}
              role="menu"
              className="fixed z-50 min-w-36 rounded-md border border-slate-200 bg-white py-1 shadow-lg"
              style={coords ? { top: coords.top, left: coords.left } : { visibility: "hidden" }}
            >
              {items.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  className={`block w-full px-3 py-1.5 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50 ${
                    item.tone === "danger"
                      ? "text-red-600 hover:bg-red-50"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                  onClick={() => {
                    setOpen(false);
                    item.onSelect();
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
